import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api, getToken } from '../lib/api';
import { cn } from '../lib/cn';
import { ruleLabel } from '../lib/rule-labels';

// --- API response types (mirror apps/worker/src/lib/dashboard-stats.ts) ---
type Platform = 'tiktok' | 'facebook' | 'other';
type Lens = 'pass' | 'block' | 'pending';
type Range = '24h' | '7d';

interface BarItem {
  key: string;
  cnt: number;
}
interface DimensionByLens {
  pass: BarItem[];
  block: BarItem[];
  pending: BarItem[];
}
interface BehaviorStats {
  avgDwellMs: number;
  avgInteractions: number;
  challengePassRate: number | null;
  sampleSize: number;
}
interface PlatformStats {
  total: number;
  pass: number;
  block: number;
  pending: number;
  passRate: number;
  datacenterPct: { pass: number; block: number; pending: number };
  mobilePct: { pass: number; block: number; pending: number };
  country: DimensionByLens;
  asn: DimensionByLens;
  os: DimensionByLens;
  language: DimensionByLens;
  timezone: DimensionByLens;
  scoreDist: BarItem[];
  topReasons: BarItem[];
  behavior: { pass: BehaviorStats; block: BehaviorStats; pending: BehaviorStats };
}
interface DashboardStats {
  overall: {
    total: number;
    pass: number;
    block: number;
    pending: number;
    passRate: number;
    datacenterPct: number;
  };
  platforms: Record<Platform, PlatformStats>;
}
interface TimeseriesResp {
  rows: { bucket: number; total: number; passCnt: number; blockCnt: number }[];
}

export const Route = createFileRoute('/')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: DashboardPage,
});

const PLATFORM_META: Record<Platform, { name: string; dot: string }> = {
  tiktok: { name: 'TikTok', dot: 'bg-cyan-400' },
  facebook: { name: 'Facebook', dot: 'bg-blue-400' },
  other: { name: '其他 / 自然', dot: 'bg-zinc-500' },
};

const RANGE_LABEL: Record<Range, string> = { '24h': '24 小时', '7d': '7 天' };

function DashboardPage() {
  const [range, setRange] = useState<Range>('24h');
  const [lens, setLens] = useState<Lens>('pass');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', range],
    queryFn: () =>
      api<DashboardStats & { range: string }>(`/api/audits/stats/dashboard?range=${range}`),
    refetchInterval: 30_000,
  });
  const { data: ts } = useQuery({
    queryKey: ['dashboard-ts', range],
    queryFn: () => api<TimeseriesResp>(`/api/audits/stats/timeseries?range=${range}`),
    refetchInterval: 60_000,
  });

  const o = data?.overall;

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">仪表盘</h1>
          <p className="mt-1 text-sm text-zinc-500">访客特征画像 · 近 {RANGE_LABEL[range]}</p>
        </div>
        <Tabs value={range} onValueChange={(v) => setRange(v as Range)}>
          <TabsList>
            <TabsTrigger value="24h">24h</TabsTrigger>
            <TabsTrigger value="7d">7d</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* 大盘 KPI */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="总访问" value={isLoading ? '…' : fmtInt(o?.total ?? 0)} />
        <Kpi
          label="通过率"
          value={isLoading ? '…' : pct(o?.passRate ?? 0)}
          valueClass="text-emerald-500"
          sub={
            o
              ? `${fmtInt(o.pass)} 通过 / ${fmtInt(o.block)} 拦截 / ${fmtInt(o.pending)} 未完成`
              : undefined
          }
        />
        <Kpi
          label="已拦截"
          value={isLoading ? '…' : fmtInt(o?.block ?? 0)}
          valueClass="text-rose-500"
        />
        <Kpi
          label="数据中心 IP 占比"
          value={isLoading ? '…' : pct(o?.datacenterPct ?? 0)}
          sub="疑似机房 / 代理流量"
        />
      </div>

      {/* 访问量趋势 */}
      <Card className="mt-4 gap-3 p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
            访问量趋势
          </span>
          <span className="text-xs text-zinc-500">近 {RANGE_LABEL[range]}</span>
        </div>
        <Sparkline rows={ts?.rows ?? []} />
      </Card>

      {/* 平台画像 */}
      <div className="mt-8 mb-3 flex items-center justify-between gap-4">
        <h2 className="text-sm font-semibold">分平台访客画像</h2>
        <div className="flex items-center gap-2">
          <span className="hidden text-xs text-zinc-500 sm:inline">画像口径</span>
          <Tabs value={lens} onValueChange={(v) => setLens(v as Lens)}>
            <TabsList>
              <TabsTrigger value="pass">真实用户(通过)</TabsTrigger>
              <TabsTrigger value="block">被拦截</TabsTrigger>
              <TabsTrigger value="pending">未完成验证</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {(['tiktok', 'facebook', 'other'] as Platform[]).map((p) => (
          <PlatformColumn
            key={p}
            platform={p}
            stats={data?.platforms[p]}
            lens={lens}
            overallTotal={o?.total ?? 0}
            loading={isLoading}
          />
        ))}
      </div>
    </div>
  );
}

// --- KPI card ---
function Kpi({
  label,
  value,
  sub,
  valueClass,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
}) {
  return (
    <Card className="gap-2 p-5">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={cn('text-3xl font-semibold', valueClass)}>{value}</div>
      {sub ? <div className="text-xs text-zinc-500">{sub}</div> : null}
    </Card>
  );
}

// --- Platform column ---
function PlatformColumn({
  platform,
  stats,
  lens,
  overallTotal,
  loading,
}: {
  platform: Platform;
  stats: PlatformStats | undefined;
  lens: Lens;
  overallTotal: number;
  loading: boolean;
}) {
  const meta = PLATFORM_META[platform];
  const share = stats && overallTotal > 0 ? stats.total / overallTotal : 0;
  const accent = lens === 'pass' ? 'emerald' : lens === 'block' ? 'rose' : 'zinc';
  const beh = stats?.behavior[lens];
  const lensCount = stats ? stats[lens] : 0;
  const lensEmpty = lensCount === 0;

  return (
    <Card className="gap-0 overflow-hidden p-0">
      {/* header */}
      <div className="border-b border-zinc-200/60 p-4 dark:border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={cn('size-2 rounded-full', meta.dot)} />
            <span className="font-semibold">{meta.name}</span>
          </div>
          <span className="text-xs text-zinc-500">占 {pct(share)}</span>
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-semibold">
            {loading ? '…' : fmtInt(stats?.total ?? 0)}
          </span>
          <span className="text-xs text-zinc-500">次访问</span>
        </div>
        {stats ? (
          <>
            <SplitBar pass={stats.pass} block={stats.block} pending={stats.pending} />
            <div className="mt-1.5 flex justify-between text-xs text-zinc-500">
              <span className="text-emerald-500">{fmtInt(stats.pass)} 通过</span>
              <span className="text-rose-500">{fmtInt(stats.block)} 拦截</span>
              <span className="text-zinc-400">{fmtInt(stats.pending)} 未完成</span>
            </div>
            <div className="mt-0.5 text-right text-xs text-zinc-500">
              通过率 {pct(stats.passRate)}
            </div>
          </>
        ) : null}
      </div>

      {lensEmpty && !loading ? (
        <div className="p-4 text-xs text-zinc-500">
          {lens === 'pass'
            ? '该来源暂无通过流量'
            : lens === 'block'
              ? '该来源暂无被拦截流量'
              : '该来源暂无未完成验证流量'}
        </div>
      ) : null}

      {stats && !lensEmpty ? (
        <>
          {/* 地理 & 网络 */}
          <Section icon="🌍" title="地理 & 网络">
            <BarList items={stats.country[lens]} accent={accent} unit="" />
            <SubStat
              label="数据中心 IP"
              value={pct(stats.datacenterPct[lens])}
              hint="疑似机房/代理"
            />
            {stats.asn[lens][0] ? (
              <SubStat label="主力运营商" value={stats.asn[lens][0].key} />
            ) : null}
          </Section>

          {/* 设备 & 系统 */}
          <Section icon="📱" title="设备 & 系统">
            <SubStat label="移动设备占比" value={pct(stats.mobilePct[lens])} />
            <BarList items={stats.os[lens]} accent={accent} />
          </Section>

          {/* 语言 & 时区 */}
          <Section icon="🗣" title="语言 & 时区">
            <BarList items={stats.language[lens]} accent={accent} />
            {stats.timezone[lens][0] ? (
              <SubStat label="主力时区" value={stats.timezone[lens][0].key} />
            ) : null}
          </Section>

          {/* 行为 */}
          <Section icon="⏱" title="行为特征">
            <SubStat label="平均停留" value={beh ? fmtDwell(beh.avgDwellMs) : '—'} />
            <SubStat label="平均交互数" value={beh ? String(beh.avgInteractions) : '—'} />
            <SubStat
              label="人机挑战通过率"
              value={beh && beh.challengePassRate != null ? pct(beh.challengePassRate) : '—'}
            />
          </Section>
        </>
      ) : null}

      {/* 流量质量 — 始终展示(含通过+拦截) */}
      {stats ? (
        <Section icon="📊" title="流量质量" subtle="不随口径切换 · 始终全量">
          <ScoreHistogram items={stats.scoreDist} />
          <div className="mt-2 space-y-1">
            <div className="text-xs text-zinc-500">拦截原因 Top</div>
            <BarList
              items={stats.topReasons.map((r) => ({ key: ruleLabel(r.key), cnt: r.cnt }))}
              accent="rose"
              empty="无拦截"
            />
          </div>
        </Section>
      ) : null}
    </Card>
  );
}

function Section({
  icon,
  title,
  subtle,
  children,
}: {
  icon: string;
  title: string;
  subtle?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2 border-b border-zinc-200/60 p-4 last:border-b-0 dark:border-zinc-800">
      <div className="flex items-baseline justify-between">
        <div className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">
          <span className="mr-1.5">{icon}</span>
          {title}
        </div>
        {subtle ? <span className="text-[10px] text-zinc-500">{subtle}</span> : null}
      </div>
      {children}
    </div>
  );
}

// --- Bar list (proportional horizontal bars) ---
function BarList({
  items,
  accent,
  unit,
  empty = '暂无数据',
}: {
  items: BarItem[];
  accent: 'emerald' | 'rose' | 'zinc';
  unit?: string;
  empty?: string;
}) {
  if (!items.length) return <div className="text-xs text-zinc-500">{empty}</div>;
  const max = Math.max(...items.map((i) => i.cnt), 1);
  const fill =
    accent === 'emerald'
      ? 'bg-emerald-500/15'
      : accent === 'rose'
        ? 'bg-rose-500/15'
        : 'bg-zinc-500/15';
  return (
    <div className="space-y-1">
      {items.map((it) => (
        <div key={it.key} className="relative overflow-hidden rounded">
          <div
            className={cn('absolute inset-y-0 left-0 rounded', fill)}
            style={{ width: `${(it.cnt / max) * 100}%` }}
          />
          <div className="relative flex items-center justify-between px-1.5 py-1 text-xs">
            <span className="truncate text-zinc-700 dark:text-zinc-200">{it.key}</span>
            <span className="ml-2 font-mono text-zinc-500">
              {fmtInt(it.cnt)}
              {unit}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-zinc-500">
        {label}
        {hint ? <span className="ml-1 text-zinc-600">({hint})</span> : null}
      </span>
      <span className="font-mono text-zinc-700 dark:text-zinc-200">{value}</span>
    </div>
  );
}

// --- Score histogram (0..100, 10-wide buckets) ---
function ScoreHistogram({ items }: { items: BarItem[] }) {
  const byBucket = new Map(items.map((i) => [Number(i.key), i.cnt]));
  const buckets = Array.from({ length: 11 }, (_, i) => i * 10);
  const max = Math.max(...items.map((i) => i.cnt), 1);
  const hasData = items.length > 0;
  return (
    <div>
      <div className="mb-1 text-xs text-zinc-500">得分分布</div>
      {hasData ? (
        <div className="flex h-12 items-end gap-px">
          {buckets.map((b) => {
            const cnt = byBucket.get(b) ?? 0;
            const h = Math.max((cnt / max) * 100, cnt > 0 ? 6 : 0);
            // 低分=偏拦截(rose),高分=偏通过(emerald)
            const color =
              b >= 60 ? 'bg-emerald-500/50' : b >= 40 ? 'bg-amber-500/50' : 'bg-rose-500/50';
            return (
              <div
                key={b}
                className={cn(
                  'flex-1 rounded-sm',
                  cnt > 0 ? color : 'bg-zinc-200/40 dark:bg-zinc-800',
                )}
                style={{ height: `${h}%` }}
                title={`${b}-${b + 9} 分: ${cnt}`}
              />
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-zinc-500">暂无数据</div>
      )}
      <div className="mt-0.5 flex justify-between text-[10px] text-zinc-500">
        <span>0</span>
        <span>50</span>
        <span>100</span>
      </div>
    </div>
  );
}

// --- Split bar (pass vs block vs pending) ---
function SplitBar({ pass, block, pending }: { pass: number; block: number; pending: number }) {
  const total = pass + block + pending;
  const track = 'mt-3 flex h-1.5 overflow-hidden rounded-full bg-zinc-200/60 dark:bg-zinc-800';
  if (total === 0) return <div className={track} />;
  const passPct = (pass / total) * 100;
  const blockPct = (block / total) * 100;
  const pendingPct = (pending / total) * 100;
  return (
    <div className={track}>
      <div className="bg-emerald-500" style={{ width: `${passPct}%` }} />
      <div className="bg-rose-500/70" style={{ width: `${blockPct}%` }} />
      <div className="bg-zinc-400/70" style={{ width: `${pendingPct}%` }} />
    </div>
  );
}

// --- Sparkline (SVG) ---
function Sparkline({ rows }: { rows: TimeseriesResp['rows'] }) {
  if (rows.length < 2) {
    return <div className="flex h-16 items-center text-xs text-zinc-500">数据点不足</div>;
  }
  const totals = rows.map((r) => r.total);
  const max = Math.max(...totals, 1);
  const W = 100;
  const H = 28;
  const step = W / (rows.length - 1);
  const pts = totals.map((t, i) => `${(i * step).toFixed(2)},${(H - (t / max) * H).toFixed(2)}`);
  const line = pts.join(' ');
  const area = `0,${H} ${line} ${W},${H}`;
  const peak = fmtInt(max);
  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-16 w-full"
        role="img"
        aria-label="访问量趋势"
      >
        <polygon points={area} fill="url(#spark)" />
        <polyline
          points={line}
          fill="none"
          stroke="rgb(16 185 129)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
        <defs>
          <linearGradient id="spark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-zinc-500">
        <span>{rows.length} 个时间桶</span>
        <span>峰值 {peak}/桶</span>
      </div>
    </div>
  );
}

// --- formatters ---
function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}
function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}
function fmtDwell(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}
